import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Building2, Loader2, Mail, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/axios";
import { toast } from "sonner";

const roleLabel: Record<string, string> = {
  influenciador: "Indicador",
  vendedor: "Vendedor",
  admin: "Administrador",
};

interface InvitationPreview {
  company_name: string;
  role: string;
}

export default function InvitePreview() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, loginWithRedirect } = useAuth0();

  const { data: preview, isLoading, isError } = useQuery({
    queryKey: ["invitation-preview", token],
    queryFn: async () => {
      const { data } = await api.get<InvitationPreview>(
        `/companies/invitations/preview/${token}`
      );
      return data;
    },
    enabled: !!token,
    retry: false,
  });

  const accept = useMutation({
    mutationFn: async () => {
      await api.post(`/companies/invitations/${token}/accept`);
    },
    onSuccess: () => {
      toast.success(`Você agora faz parte de ${preview?.company_name}!`);
      navigate("/");
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        "Erro ao aceitar convite.";
      toast.error(msg);
    },
  });

  function handleAccept() {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: `/convite/${token}` },
      });
      return;
    }
    accept.mutate();
  }

  if (isLoading || authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="max-w-md w-full text-center">
          <CardHeader>
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
            <CardTitle>Convite inválido</CardTitle>
            <CardDescription>
              Este link de convite não existe, já foi usado ou foi revogado.
            </CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Button variant="outline" onClick={() => navigate("/")}>
              Ir para o início
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto p-3 bg-primary/10 rounded-full w-fit mb-3">
            <Mail className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Você foi convidado!</CardTitle>
          <CardDescription>
            Você recebeu um convite para fazer parte de uma equipe no IndiQR.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4 pt-4">
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Empresa</p>
                <p className="font-semibold">{preview?.company_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Função</p>
                <Badge variant="secondary" className="mt-0.5">
                  {roleLabel[preview?.role ?? ""] ?? preview?.role}
                </Badge>
              </div>
            </div>
          </div>

          {!isAuthenticated && (
            <p className="text-sm text-muted-foreground text-center">
              Você precisará entrar com sua conta para aceitar o convite.
            </p>
          )}
        </CardContent>

        <CardFooter className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={() => navigate("/")}>
            Recusar
          </Button>
          <Button onClick={handleAccept} disabled={accept.isPending}>
            {accept.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isAuthenticated ? (
              "Aceitar convite"
            ) : (
              "Entrar para aceitar"
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
